import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Light theme definition
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
  },
});

// Dark theme definition
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
      light: '#e3f2fd',
      dark: '#42a5f5',
    },
    secondary: {
      main: '#f48fb1',
      light: '#fce4ec',
      dark: '#ad1457',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          borderRadius: 8,
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const { user, updateProfile } = useAuth();
  const [currentTheme, setCurrentTheme] = useState('light');
  const [loading, setLoading] = useState(false);

  // Initialize theme from user preferences
  useEffect(() => {
    if (user?.preferences?.theme) {
      setCurrentTheme(user.preferences.theme);
    }
  }, [user]);

  // Persist the theme with a single PUT /auth/profile (the backend merges the
  // preferences subtree and AuthContext updates `user` from the response).
  const updateThemePreference = async (theme) => {
    if (theme === currentTheme) return;
    const previous = currentTheme;
    try {
      setLoading(true);
      setCurrentTheme(theme); // immediate feedback
      await updateProfile({ preferences: { theme } });
    } catch (error) {
      console.error('Failed to update theme preference:', error);
      setCurrentTheme(previous);
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = () => updateThemePreference(currentTheme === 'light' ? 'dark' : 'light');

  const theme = currentTheme === 'dark' ? darkTheme : lightTheme;

  const contextValue = {
    currentTheme,
    toggleTheme,
    updateThemePreference,
    loading,
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light'
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
